import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, KeyRound, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { authRepository } from "@/data/repositories";

export const Route = createFileRoute("/acceso")({
  head: () => ({
    meta: [{ title: "Acceso privado — SUSIEDAD" }, { name: "robots", content: "noindex,nofollow" }],
  }),
  component: AccessPage,
});

function AccessPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "forgot" | "reset">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const queryMode = new URLSearchParams(window.location.search).get("mode");

    // Quien llega desde una invitación o desde un enlace de recuperación trae ya
    // una sesión válida en la URL. Si se le redirige por tenerla, entra al portal
    // sin haber puesto contraseña y no puede volver a entrar nunca más. Aquí se
    // corta el redirigido y se le pide la contraseña primero.
    if (queryMode === "reset") {
      setMode("reset");
      return;
    }

    authRepository.getViewer().then((viewer) => {
      if (viewer) navigate({ to: viewer.isAdmin ? "/app" : "/portal", replace: true });
    });
  }, [navigate]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await authRepository.requestPasswordReset(email);
        toast.success("Revisa tu correo para recuperar el acceso");
        setMode("signin");
      } else if (mode === "reset") {
        await authRepository.updatePassword(password);
        toast.success("Contraseña actualizada");
        const viewer = await authRepository.getViewer();
        navigate({ to: viewer?.isAdmin ? "/app" : "/portal", replace: true });
      } else {
        const viewer = await authRepository.signIn(email, password);
        navigate({ to: viewer.isAdmin ? "/app" : "/portal", replace: true });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo iniciar sesión");
    } finally {
      setLoading(false);
    }
  }

  const title =
    mode === "signin"
      ? "Tu acceso al backstage."
      : mode === "forgot"
        ? "Recupera tu acceso."
        : "Elige tu contraseña.";

  return (
    <main className="grid min-h-screen bg-bone lg:grid-cols-[1.05fr_0.95fr]">
      <section className="relative hidden overflow-hidden bg-onyx p-12 text-bone lg:flex lg:flex-col lg:justify-between">
        <div className="absolute -right-32 top-20 size-[34rem] rounded-full border border-neon/30" />
        <div className="absolute -right-16 top-36 size-[23rem] rounded-full border border-neon/50" />
        <BrandLogo className="relative h-8 w-auto" />
        <div className="relative max-w-2xl">
          <p className="eyebrow !text-neon">CRM · Portal de clientes</p>
          <h1 className="mt-6 font-display text-7xl leading-[0.88] tracking-[-0.06em]">
            TODO LISTO PARA EL MOMENTO EXACTO.
          </h1>
          <p className="mt-8 max-w-lg text-xl text-bone/65">
            Planificación, música y decisiones importantes del evento en un espacio privado.
          </p>
        </div>
        <p className="relative font-mono text-[10px] uppercase tracking-[0.22em] text-bone/40">
          Málaga · Toda España
        </p>
      </section>

      <section className="flex min-h-screen items-center justify-center px-5 py-12 sm:px-12">
        <div className="w-full max-w-md">
          <a href="/" className="mb-14 inline-flex lg:hidden" aria-label="SUSIEDAD · Inicio">
            <BrandLogo className="h-6 w-auto" />
          </a>
          <p className="eyebrow text-primary">Acceso privado</p>
          <h2 className="mt-4 font-display text-5xl leading-[0.9]">{title}</h2>
          <p className="mt-4 text-muted-foreground">
            {mode === "signin"
              ? "Accede con la cuenta invitada a tu evento o con tu cuenta de administración."
              : mode === "forgot"
                ? "Te enviaremos instrucciones seguras por email."
                : "Guarda una contraseña para volver a entrar cuando quieras."}
          </p>

          <form className="mt-9 space-y-5" onSubmit={submit}>
            {mode !== "reset" && (
              <label className="block">
                <span className="eyebrow mb-2 block text-foreground">Email</span>
                <span className="relative block">
                  <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="field pl-10"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </span>
              </label>
            )}
            {mode !== "forgot" && (
              <label className="block">
                <span className="eyebrow mb-2 block text-foreground">Contraseña</span>
                <span className="relative block">
                  <KeyRound className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    className="field pl-10"
                    type="password"
                    autoComplete={mode === "reset" ? "new-password" : "current-password"}
                    minLength={8}
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </span>
              </label>
            )}
            <button className="btn-primary w-full" disabled={loading}>
              {loading
                ? "Procesando…"
                : mode === "signin"
                  ? "Entrar"
                  : mode === "forgot"
                    ? "Enviar enlace"
                    : "Guardar contraseña"}
              <ArrowRight className="size-4" />
            </button>
          </form>

          {mode === "signin" && (
            <>
              <button
                className="mt-5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                onClick={() => setMode("forgot")}
              >
                He olvidado mi contraseña
              </button>
            </>
          )}
          {mode !== "signin" && (
            <button
              className="mt-5 text-sm text-muted-foreground hover:text-foreground"
              onClick={() => setMode("signin")}
            >
              ← Volver al acceso
            </button>
          )}
          <p className="mt-12 border-t border-border pt-5 text-sm text-muted-foreground">
            No existe registro público. SUSIEDAD crea e invita cada cuenta de forma privada.
          </p>
        </div>
      </section>
    </main>
  );
}
